import { useEffect, useMemo, useState } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';

import type { LocationSearchPractice } from '@/react-query/useReferralInfo';

import { useLocationSpecialistSearch } from '../../hooks/useLocationSpecialistSearch';
import type { ReferralFormValues } from '../../types';

import { LocationSearchFilters } from './LocationSearchFilters';
import { formatPracticeAddress } from './locationSearchUtils';
import { SpecialistSelectionForm } from './SpecialistSelectionForm';

interface TreatingSpecialistTabV2Props {
  onPracticeSelect?: (
    practice:
      | {
          practice_id: number;
          practice_name: string;
          referral_practice_address?: string;
          referral_practice_phone?: string;
        }
      | undefined,
  ) => void;
  videaPracticeId: string;
}

export const TreatingSpecialistTabV2 = ({
  onPracticeSelect,
  videaPracticeId,
}: TreatingSpecialistTabV2Props) => {
  const [specialtySearch, setSpecialtySearch] = useState('');
  const [practiceSearch, setPracticeSearch] = useState('');
  const [specialistSearch, setSpecialistSearch] = useState('');

  const {
    setValue,
    formState: { errors },
  } = useFormContext<ReferralFormValues>();

  const [selectedPractice, setSelectedPractice] =
    useState<LocationSearchPractice | null>(null);

  // Watch the selected values from the form
  const selectedSpecialtyId = useWatch({ name: 'specialtyId' });
  const selectedSpecialtyName = useWatch({ name: 'specialtyName' });
  const selectedPracticeId = useWatch({ name: 'referralPracticeId' });
  const useNextAvailableSpecialist = useWatch({
    name: 'useNextAvailableSpecialist',
  });
  const selectedSpecialistId = useWatch({ name: 'referralSpecialistId' });

  // Sync specialty search field with the selected specialty name when navigating back or initializing
  // This ensures the Autocomplete displays the selected value
  useEffect(() => {
    if (selectedSpecialtyName && !specialtySearch) {
      setSpecialtySearch(selectedSpecialtyName);
    }
  }, [selectedSpecialtyName, specialtySearch]);

  // Use custom hook for location-based search logic
  const {
    postalCode,
    setPostalCode,
    debouncedPostalCode,
    radius,
    setRadius,
    locationSearchResults,
    specialties,
    practices,
    specialists,
    isSearchLoading,
    isSearchFetching,
    isSpecialtiesLoading,
    isPracticesLoading,
  } = useLocationSpecialistSearch({
    videaPracticeId,
    selectedSpecialtyId,
    selectedPractice,
  });

  const specialtiesWithSelected = useMemo(() => {
    if (!selectedSpecialtyId) return specialties;

    const hasSelectedSpecialty = specialties.some(
      (s) => s.id === selectedSpecialtyId,
    );
    if (hasSelectedSpecialty) return specialties;

    // Add selected specialty to the list if it's not there yet
    if (selectedSpecialtyName) {
      return [
        { id: selectedSpecialtyId, name: selectedSpecialtyName },
        ...specialties,
      ];
    }

    // Placeholder while name loads
    return [{ id: selectedSpecialtyId, name: `Loading...` }, ...specialties];
  }, [selectedSpecialtyId, selectedSpecialtyName, specialties]);

  // Backfill specialty name if we have ID but no name (e.g. navigating back)
  useEffect(() => {
    if (!selectedSpecialtyId || selectedSpecialtyName) return;

    const specialty = specialties.find((s) => s.id === selectedSpecialtyId);
    if (!specialty) return;

    setValue('specialtyName', specialty.name);
  }, [selectedSpecialtyId, selectedSpecialtyName, setValue, specialties]);

  // Reset selected practice when postal code or radius changes
  useEffect(() => {
    if (selectedPractice) {
      setSelectedPractice(null);
      setValue('referralPracticeId', undefined);
      setValue('referralSpecialistId', undefined);
      setValue('useNextAvailableSpecialist', false);
      setValue('referralPracticeAddress', '');
      setValue('referralPracticePhone', '');
      onPracticeSelect?.(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPostalCode, radius]);

  // Handler functions
  const handleSpecialtyChange = (specialtyId?: string) => {
    const parsedSpecialtyId = specialtyId
      ? parseInt(specialtyId, 10)
      : undefined;

    setValue('specialtyId', parsedSpecialtyId);

    const specialty =
      parsedSpecialtyId === undefined
        ? undefined
        : (specialtiesWithSelected.find((s) => s.id === parsedSpecialtyId) ??
          specialties.find((s) => s.id === parsedSpecialtyId));
    setValue('specialtyName', specialty?.name);

    setValue('proceduresIds', []);
    setValue('referralPracticeId', undefined);
    setValue('referralSpecialistId', undefined);
    setValue('useNextAvailableSpecialist', false);
    setValue('referralPracticeAddress', '');
    setValue('referralPracticePhone', '');
    setSelectedPractice(null);
    onPracticeSelect?.(undefined);
  };

  const handlePracticeChange = (practiceId?: string) => {
    const practice = practices.find((p) => p.id.toString() === practiceId);

    setValue(
      'referralPracticeId',
      practiceId ? parseInt(practiceId, 10) : undefined,
    );
    setValue('referralSpecialistId', undefined);
    setValue('useNextAvailableSpecialist', false);

    if (practice) {
      const fullAddress = formatPracticeAddress(practice);

      setValue('referralPracticeAddress', fullAddress || '');
      setValue('referralPracticePhone', practice.phone || '');
      setSelectedPractice(practice);
      onPracticeSelect?.({
        practice_id: practice.id,
        practice_name: practice.practiceName,
        referral_practice_address: fullAddress,
        referral_practice_phone: practice.phone || undefined,
      });
    } else {
      setValue('referralPracticeAddress', '');
      setValue('referralPracticePhone', '');
      setSelectedPractice(null);
      onPracticeSelect?.(undefined);
    }
  };

  const handleSpecialistChange = (specialistId?: string) => {
    setValue(
      'referralSpecialistId',
      specialistId ? parseInt(specialistId, 10) : undefined,
    );
  };

  const handleNextAvailableToggle = (checked: boolean) => {
    setValue('useNextAvailableSpecialist', checked);
    if (checked) {
      setValue('referralSpecialistId', undefined);
    }
  };

  return (
    <div className="vai-flex vai-gap-6">
      <LocationSearchFilters
        postalCode={postalCode}
        onPostalCodeChange={(p: string) => {
          setPostalCode(p);
          setSpecialtySearch('');
          setPracticeSearch('');
          setSpecialistSearch('');
          handleSpecialtyChange(undefined);
          handlePracticeChange(undefined);
          handleSpecialistChange(undefined);
        }}
        radius={radius}
        onRadiusChange={(r: string) => {
          setRadius(r);
          setSpecialtySearch('');
          setPracticeSearch('');
          setSpecialistSearch('');
          handleSpecialtyChange(undefined);
          handlePracticeChange(undefined);
          handleSpecialistChange(undefined);
        }}
        isSearching={isSearchFetching}
      />

      <SpecialistSelectionForm
        specialties={specialtiesWithSelected}
        practices={practices}
        specialists={specialists}
        selectedSpecialtyId={selectedSpecialtyId}
        selectedPracticeId={selectedPracticeId}
        selectedSpecialistId={selectedSpecialistId}
        useNextAvailableSpecialist={useNextAvailableSpecialist}
        onSpecialtyChange={handleSpecialtyChange}
        onPracticeChange={handlePracticeChange}
        onSpecialistChange={handleSpecialistChange}
        onNextAvailableToggle={handleNextAvailableToggle}
        postalCode={postalCode}
        debouncedPostalCode={debouncedPostalCode}
        isSpecialtiesLoading={isSpecialtiesLoading}
        isPracticesLoading={isPracticesLoading}
        isSearchLoading={isSearchLoading}
        locationSearchResults={locationSearchResults}
        errors={errors}
        specialtySearch={specialtySearch}
        setSpecialtySearch={setSpecialtySearch}
        practiceSearch={practiceSearch}
        setPracticeSearch={setPracticeSearch}
        specialistSearch={specialistSearch}
        setSpecialistSearch={setSpecialistSearch}
      />
    </div>
  );
};
